import React, { useEffect, useMemo, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { withRouter, Link } from 'react-router-dom'
import { withTranslation } from 'react-i18next'
import _ from 'lodash'
import ProLayout from '@ant-design/pro-layout'
import { App as AntdApp, ConfigProvider, Grid, theme as antdTheme } from 'antd'
import enUS from 'antd/locale/en_US'
import thTH from 'antd/locale/th_TH'
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/th'
import {
    UserAddOutlined,
    DatabaseOutlined,
    DollarOutlined,
    WalletOutlined,
    TeamOutlined,
    ShareAltOutlined,
    RiseOutlined,
    SolutionOutlined,
    PushpinOutlined,
    FileTextOutlined,
    PrinterOutlined,
    ContainerOutlined,
    BookOutlined,
    CarOutlined,
    LikeOutlined,
    UsergroupAddOutlined,
    UnorderedListOutlined,
    ThunderboltOutlined,
    GlobalOutlined,
    LogoutOutlined,
    BulbOutlined,
    TrophyOutlined,
    CommentOutlined,
    CalendarOutlined
} from '@ant-design/icons'
import { ThemeProvider } from 'styled-components'
import { getTheme } from '../../../config/theme/themedefault'
import { siteConfig } from '../../../config/index'
import appActions from '../../redux/app/actions'
import authAction from '../../redux/auth/actions'
import currentStaffContainer from '../CurrentStaff/CurrentStaffContainer'
import AlertContainer from '../Notifications/AlertContainer'

const { toggleAll, changeCurrent, toggleDarkMode } = appActions
const { logout } = authAction

const buildRoutes = ({ i18n, isSuperAdmin, rolesMatched, isCallCenter, darkMode }) => {
    if (isCallCenter) {
        return [
            {
                path: '/customers',
                name: i18n.t('customer'),
                icon: <UserAddOutlined />
            },
            {
                path: '/tickets',
                name: i18n.t('tickets'),
                icon: <UnorderedListOutlined />
            },
            {
                path: '/projects',
                name: i18n.t('projects'),
                icon: <DatabaseOutlined />
            },
            {
                path: '/shipping_rates',
                name: i18n.t('shippingRates'),
                icon: <DatabaseOutlined />
            }
        ]
    }

    const customerSale = {
        path: '/customer-sale',
        name: i18n.t('menuCustomerSale'),
        icon: <UserAddOutlined />,
        routes: [
            { path: '/customers', name: i18n.t('customer'), icon: <UserAddOutlined /> },
            { path: '/deals', name: i18n.t('deals'), icon: <DatabaseOutlined /> },
            rolesMatched(['super_admin', 'admin']) && {
                path: '/lead_distributions', name: i18n.t('leadDistributions'), icon: <UsergroupAddOutlined />
            },
            rolesMatched(['super_admin', 'admin']) && {
                path: '/high_value_baskets', name: i18n.t('highValueBaskets'), icon: <TrophyOutlined />
            },
            rolesMatched(['super_admin', 'admin']) && {
                path: '/line_items', name: i18n.t('lineItems'), icon: <UnorderedListOutlined />
            },
            { path: '/tickets', name: i18n.t('tickets'), icon: <UnorderedListOutlined /> },
            { path: '/order_summaries', name: i18n.t('orders'), icon: <DatabaseOutlined /> },
            { path: '/sale_pipelines', name: i18n.t('salePipelines'), icon: <DollarOutlined /> },
            { path: '/sales_summary', name: i18n.t('salesSummary'), icon: <DollarOutlined /> },
            { path: '/wallet_summaries', name: i18n.t('walletSummaries'), icon: <WalletOutlined /> },
            { path: '/companies', name: i18n.t('companies'), icon: <TeamOutlined /> },
            { path: '/projects', name: i18n.t('projects'), icon: <DatabaseOutlined /> },
            isSuperAdmin && { path: '/profit_sharings', name: i18n.t('profitSharing'), icon: <ShareAltOutlined /> },
            isSuperAdmin && { path: '/orders/yields', name: i18n.t('yields'), icon: <RiseOutlined /> },
            { path: '/event_logs', name: i18n.t('eventLogs'), icon: <SolutionOutlined /> },
            { path: '/line_pins', name: i18n.t('linePins'), icon: <PushpinOutlined /> }
        ].filter(Boolean)
    }

    const vendor = {
        path: '/vendor',
        name: i18n.t('menuVendor'),
        icon: <TeamOutlined />,
        routes: [
            { path: '/supplier_order_reviews', name: i18n.t('purchaseOrders'), icon: <FileTextOutlined />, target: '_blank' },
            { path: '/supplier_orders', name: i18n.t('supplierOrders'), icon: <FileTextOutlined /> },
            { path: '/trucks', name: i18n.t('trucks'), icon: <CarOutlined /> },
            { path: '/suppliers', name: i18n.t('suppliers'), icon: <TeamOutlined /> }
        ]
    }

    const account = {
        path: '/account',
        name: i18n.t('menuAccount'),
        icon: <ContainerOutlined />,
        routes: [
            { path: '/orders/documents', name: i18n.t('orderDocuments'), icon: <PrinterOutlined /> },
            { path: '/supplier_invoices', name: i18n.t('supplierInvoices'), icon: <FileTextOutlined /> },
            { path: '/order_payment_details', name: i18n.t('orderPaymentDetails'), icon: <ContainerOutlined /> },
            { path: '/payment_vouchers', name: i18n.t('paymentVouchers'), icon: <ContainerOutlined /> },
            { path: '/supplier_payments', name: i18n.t('supplierPayments'), icon: <DollarOutlined /> },
            rolesMatched(['super_admin', 'accounting']) && {
                path: '/tax_invoices', name: i18n.t('taxInvoices'), icon: <FileTextOutlined />
            },
            { path: '/vat_documents', name: i18n.t('vatDocuments'), icon: <PrinterOutlined /> },
            { path: '/bank_statements', name: i18n.t('bankStatements'), icon: <BookOutlined /> },
            { path: '/withholding_taxes', name: i18n.t('withholdingTaxes'), icon: <DatabaseOutlined /> },
            { path: '/chart_of_accounts', name: i18n.t('chartOfAccounts'), icon: <ContainerOutlined /> },
            { path: '/journal_entry_formats', name: i18n.t('journalEntryFormats'), icon: <DatabaseOutlined /> },
            { path: '/journal_entries', name: i18n.t('journalEntries'), icon: <DatabaseOutlined /> },
            rolesMatched(['super_admin', 'loan_agreement']) && {
                path: '/loan_agreements', name: i18n.t('loanAgreements'), icon: <FileTextOutlined />
            }
        ].filter(Boolean)
    }

    const feedback = {
        path: '/feedback',
        name: i18n.t('menuFeedback'),
        icon: <LikeOutlined />,
        routes: [
            { path: '/order_reviews', name: i18n.t('orderReviews'), icon: <LikeOutlined /> },
            { path: '/request_items', name: i18n.t('requestItemIndex'), icon: <FileTextOutlined /> },
            { path: '/customer_voices', name: i18n.t('customerVoices'), icon: <CommentOutlined /> }
        ]
    }

    const hr = {
        path: '/hr',
        name: i18n.t('menuHr'),
        icon: <CalendarOutlined />,
        routes: [
            { path: '/leave_days', name: i18n.t('leaveDays'), icon: <CalendarOutlined /> },
            rolesMatched(['hr']) && {
                path: '/employee_contracts', name: i18n.t('employeeContracts'), icon: <SolutionOutlined />
            }
        ].filter(Boolean)
    }

    const setting = {
        path: '/setting',
        name: i18n.t('menuSetting'),
        icon: <DatabaseOutlined />,
        routes: [
            { path: '/shipping_rates', name: i18n.t('shippingRates'), icon: <DatabaseOutlined /> },
            rolesMatched(['super_admin', 'admin']) && {
                path: '/customer_shipping_rates', name: i18n.t('customerShippingRates'), icon: <DatabaseOutlined />
            },
            isSuperAdmin && { path: '/staffs', name: i18n.t('staffs'), icon: <UsergroupAddOutlined /> },
            { path: '__lang__', name: i18n.t('change-locale'), icon: <GlobalOutlined />, isLanguageToggle: true },
            { path: '__theme__', name: i18n.t(darkMode ? 'lightMode' : 'darkMode'), icon: <BulbOutlined />, isThemeToggle: true },
            { path: '__logout__', name: i18n.t('logout'), icon: <LogoutOutlined />, isLogout: true }
        ].filter(Boolean)
    }

    return [customerSale, vendor, account, feedback, hr, setting]
}

const GOD_OF_WEALTH_IMG = 'https://onestockhome-production.s3-ap-southeast-1.amazonaws.com/messageImage_1572418503036.jpg'
const GOD_OF_WEALTH_IMG_2 = 'https://onestockhome-production.s3.ap-southeast-1.amazonaws.com/S__21725189.jpg'

const SidebarImages = ({ collapsed }) => {
    if (collapsed) return null
    return (
        <div style={{ padding: '8px 0', textAlign: 'center' }}>
            <img src={GOD_OF_WEALTH_IMG} alt='God of Wealth' style={{ width: 120, marginBottom: 8 }} />
            <img src={GOD_OF_WEALTH_IMG_2} alt='God of Wealth' style={{ width: 120 }} />
        </div>
    )
}

const getInitials = (currentStaff) => {
    const first = (currentStaff.get('first_name') || '').trim()
    const last = (currentStaff.get('last_name') || '').trim()
    const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
    return initials || '?'
}

const { useBreakpoint } = Grid

const AppLayout = ({ App, i18n, isCurrentStaffA, rolesMatched, currentStaff, location, children }) => {
    const dispatch = useDispatch()
    const [collapsed, setCollapsed] = useState(false)
    const screens = useBreakpoint()
    const isMobile = !screens.sm

    useEffect(() => {
        const handleResize = _.debounce(() => {
            dispatch(toggleAll(window.innerWidth, window.innerHeight))
        }, 90)
        dispatch(toggleAll(window.innerWidth, window.innerHeight))
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
            handleResize.cancel?.()
        }
    }, [dispatch])

    const isCallCenter = isCurrentStaffA('call_center')
    const isSuperAdmin = isCurrentStaffA('super_admin')
    const darkMode = App.get('darkMode')

    useEffect(() => {
        document.body.classList.toggle('dark-mode', !!darkMode)
        const tokens = getTheme(darkMode).tokens
        Object.entries(tokens).forEach(([key, value]) => {
            const cssVar = `--theme-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`
            document.body.style.setProperty(cssVar, value)
        })
        return () => document.body.classList.remove('dark-mode')
    }, [darkMode])
    const antdLocale = i18n.language === 'th' ? thTH : enUS
    dayjs.locale(i18n.language === 'th' ? 'th' : 'en')
    const routes = useMemo(
        () => buildRoutes({ i18n, isSuperAdmin, rolesMatched, isCallCenter, darkMode }),
        [i18n, i18n.language, isSuperAdmin, rolesMatched, isCallCenter, darkMode]
    )

    if (App.get('height') === 0) {
        return <div />
    }

    const initials = getInitials(currentStaff)
    const activeTheme = getTheme(darkMode)

    return (
        <ThemeProvider theme={activeTheme}>
            <ConfigProvider
                locale={antdLocale}
                theme={{ algorithm: darkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
                <ProLayout
                    title={siteConfig.siteName}
                    pageTitleRender={() => document.title}
                    logo={false}
                    layout='side'
                    fixSiderbar
                    fixedHeader
                    siderWidth={260}
                    breakpoint='md'
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    location={{ pathname: location.pathname }}
                    route={{ path: '/', routes }}
                    avatarProps={{
                        title: initials,
                        size: 'small'
                    }}
                    actionsRender={() => []}
                    menuItemRender={(item, dom) => {
                        if (item.isLanguageToggle) {
                            return (
                                <a
                                    onClick={(e) => {
                                        e.preventDefault()
                                        i18n.changeLanguage(i18n.language === 'th' ? 'en' : 'th')
                                    }}>
                                    {dom}
                                </a>
                            )
                        }
                        if (item.isLogout) {
                            return (
                                <a
                                    onClick={(e) => {
                                        e.preventDefault()
                                        dispatch(logout())
                                    }}>
                                    {dom}
                                </a>
                            )
                        }
                        if (item.isThemeToggle) {
                            return (
                                <a
                                    onClick={(e) => {
                                        e.preventDefault()
                                        dispatch(toggleDarkMode())
                                    }}>
                                    {dom}
                                </a>
                            )
                        }
                        if (!item.path || item.path.startsWith('/customer-sale') || ['/vendor', '/account', '/feedback', '/setting'].includes(item.path)) {
                            return dom
                        }
                        return (
                            <Link
                                to={item.path}
                                target={item.target}
                                onClick={() => dispatch(changeCurrent([item.path.replace(/^\//, '')]))}>
                                {dom}
                            </Link>
                        )
                    }}
                    menuHeaderRender={(_logo, titleNode) => (
                        <Link to={siteConfig.dashboard}>
                            {titleNode}
                        </Link>
                    )}
                    menuExtraRender={() => <SidebarImages collapsed={collapsed} />}
                    breadcrumbRender={(routers = []) => routers}
                    contentStyle={{
                        background: activeTheme.background.content,
                        paddingBlock: isMobile ? 8 : 32,
                        paddingInline: isMobile ? 8 : 40
                    }}>
                    <AntdApp>
                        <AlertContainer />
                        {children}
                    </AntdApp>
                </ProLayout>
            </ConfigProvider>
        </ThemeProvider>
    )
}

const mapStateToProps = (state) => ({
    App: state.get('App')
})

export default connect(mapStateToProps)(
    withRouter(withTranslation()(currentStaffContainer(AppLayout)))
)
